import React from 'react';
import PropTypes from 'prop-types';
import ProductList from './ProductList';
import BEMHelper from '@ynap/bem-helper';
import { Link } from '@ynap/router';
import { ProductGrid } from '@ynap/product-listing-page';
import { SimpleAsyncButton } from '@ynap/button';
import { ScrollToTop } from '@ynap/scroll-to-top';
import { parseUrl } from 'query-string';
import { performance } from '@ynap/clientside-monitoring';
import { withOptimizely } from '@ynap/optimizely-utils';
import { useWishList } from '@ynap/api-hooks';
import { useSelector } from 'react-redux';
import { useConfig } from '@ynap/immutables-provider';
import LoadMoreButton from './LoadMoreButton';

export const ProductListWithLoadMore = (props) => {
    const bem = new BEMHelper('ProductListWithLoadMore');
    const {
        ProductSummary,
        preloadedProductItems,
        productLists,
        productImageAspectRatio,
        imageSrcsetSize,
        messages,
        disableSPA,
        locale,
        baseUrl,
        paginationData,
        metaData,
        enableLoadMore,
        renderCoreMediaContent,
        mainContent,
        setListingHeight,
        trackProductReplacementClickEvent,
        productReplacementConfig,
        ssr,
        urlConfig,
        internal_urls,
        imageQualityPercentage,
        wishlist,
        country,
        fullbleed,
    } = props;

    const config = useConfig();
    const fullBleedModifier = fullbleed ? 'fullBleed' : '';

    return productLists.map((pl, index) => {
        const parsedUrlObject = parseUrl(pl.key);
        const pageNumber = parsedUrlObject.query.pageNumber;

        const rules = (component) => ({
            ...component,
            numOfProducts: pl.products.length,
            onTargetClick: trackProductReplacementClickEvent,
            locale,
            internal_urls,
            urlConfig,
            productReplacementConfig,
            pageNumber,
            enableLoadMore,
            imageQualityPercentage,
            fullbleed,
        });
        const loadingClass = pl.loading ? 'loading' : null;
        return (
            <ProductGrid
                setListingHeight={setListingHeight}
                index={index}
                className={bem('listingGrid', [loadingClass, fullBleedModifier])}
                key={pl.key}
                url={metaData.canonicalUrl}
                numberOfItems={paginationData.recordSetTotal}
                wishlist={wishlist}
                columns={fullbleed ? 4 : config?.head?.plp?.productGridColumns}
            >
                <ProductList
                    {...props}
                    ProductSummary={ProductSummary}
                    products={pl.products}
                    className={bem('productItem')}
                    preloadedProductItems={preloadedProductItems}
                    productImageAspectRatio={productImageAspectRatio}
                    productListKey={pl.key}
                    messages={messages}
                    pageNumber={pageNumber}
                    loading={pl.loading}
                    imageSrcsetSize={imageSrcsetSize}
                    disableSPA={disableSPA}
                    locale={locale}
                    baseUrl={baseUrl}
                    ssr={ssr}
                    wishlist={wishlist}
                    listingPage
                    country={country}
                />
                {!pl.loading && renderCoreMediaContent(mainContent, rules)}
            </ProductGrid>
        );
    });
};

const ButtonType = {
    next: 'next',
    previous: 'previous',
};

const ProductListLayout = (props) => {
    const bem = new BEMHelper('ProductListLayout');
    const {
        productLists = [],
        messages,
        loading,
        preloadedProductItems,
        productImageAspectRatio,
        disableSPA,
        paginationData,
        locale,
        metaData,
        baseUrl,
        fetchMoreListing,
        enableLoadMore,
        generateListingUrl,
        ProductSummary,
        imageSrcsetSize,
        firstFetchedPageNumber,
        lastFetchedPageNumber,
        footerAd,
        renderCoreMediaContent,
        mainContent,
        trackProductReplacementClickEvent,
        trackLoadMoreEvent,
        setListingHeight,
        currentPage,
        trackBackToTopEvent,
        forceLogin,
        productReplacementConfig,
        ssr,
        urlConfig,
        internal_urls,
        customPageSize,
        optimizely,
        country,
        omnibusConfig,
        fullbleed,
    } = props;

    const totalNumberOfViewedProducts = lastFetchedPageNumber * paginationData.pageSize;
    const copy = messages.productListing.youHaveViewed({ totalNumberOfViewedProducts, totalProducts: paginationData.recordSetTotal });
    const percentage = ((lastFetchedPageNumber * paginationData.pageSize) / paginationData.recordSetTotal) * 100;
    const uberToken = useSelector((state) => state?.auth?.uberToken);
    const wishlist = useWishList(uberToken);
    const loadingColors = useSelector((state) => state?.plp?.listing?.siblingsProducts?.loading);

    const moreButtonProps = {
        type: 'primary',
        loading,
        copy: messages.productListing.loadMore(),
        onClick: () => loadPage(ButtonType.next, customPageSize),
    };

    const previousButtonProps = {
        type: 'primary',
        loading,
        copy: messages.productListing.loadPrevious(),
        onClick: () => loadPage(ButtonType.previous, customPageSize),
    };

    const totalPages = paginationData && paginationData.totalPages;
    const previousPage = firstFetchedPageNumber > 1 ? firstFetchedPageNumber - 1 : 1;
    const nextPage = lastFetchedPageNumber < totalPages ? lastFetchedPageNumber + 1 : totalPages;
    const previousPageParams = previousPage > 1 ? { replaceParams: { pageNumber: previousPage } } : { clearParamsByKey: ['pageNumber'] };
    const previousPageLink = generateListingUrl(previousPageParams);
    const nextPageLink = generateListingUrl({ replaceParams: { pageNumber: nextPage, pageSize: customPageSize } });
    const prevent = (e) => e.preventDefault();

    const loadPage = (buttonType = ButtonType.next, customPageSize) => {
        const buttonTrackMap = {
            previous: 'load less',
            next: 'load more',
        };

        const buttonTrackValue = buttonTrackMap[buttonType];

        performance.intTiming.markSpaInteractionTime(buttonTrackValue);
        trackLoadMoreEvent(buttonTrackValue, currentPage);

        const optimizelyEventsMap = {
            previous: 'view_previous_plp',
            next: 'view_more_plp',
        };

        if (optimizely) optimizely?.track?.(optimizelyEventsMap[buttonType]);

        const isPrevious = buttonType === ButtonType.previous;

        return fetchMoreListing(isPrevious, customPageSize);
    };

    const showLoadPreviousButton = enableLoadMore && firstFetchedPageNumber > 1;
    const showLoadMoreButton = enableLoadMore && lastFetchedPageNumber < totalPages;
    const showScrollTopButton = enableLoadMore && paginationData.recordSetCount > 16;

    return (
        <>
            {showLoadPreviousButton && (
                <Link className={bem('loadPrevious')} to={previousPageLink} onClick={prevent}>
                    <SimpleAsyncButton containerClass={bem('loadMoreCTA')} className={bem('loadMoreButton')} {...previousButtonProps} />
                </Link>
            )}
            <ProductListWithLoadMore
                wishlist={wishlist}
                paginationData={paginationData}
                metaData={metaData}
                ProductSummary={ProductSummary}
                productLists={productLists}
                className={bem('productItem')}
                preloadedProductItems={preloadedProductItems}
                productImageAspectRatio={productImageAspectRatio}
                messages={messages}
                imageSrcsetSize={imageSrcsetSize}
                disableSPA={disableSPA}
                locale={locale}
                baseUrl={baseUrl}
                fetchMore={fetchMoreListing}
                totalPages={totalPages}
                enableLoadMore={enableLoadMore}
                renderCoreMediaContent={renderCoreMediaContent}
                trackProductReplacementClickEvent={trackProductReplacementClickEvent}
                mainContent={mainContent}
                setListingHeight={setListingHeight}
                loading={loading}
                forceLogin={forceLogin}
                productReplacementConfig={productReplacementConfig}
                ssr={ssr}
                urlConfig={urlConfig}
                internal_urls={internal_urls}
                country={country}
                omnibusConfig={omnibusConfig}
                loadingColors={loadingColors}
                fullbleed={fullbleed}
            />

            {showLoadMoreButton && (
                <LoadMoreButton copy={copy} moreButtonProps={moreButtonProps} nextPageLink={nextPageLink} percentage={percentage} prevent={prevent} />
            )}
            {showScrollTopButton && <ScrollToTop trackBackToTopEvent={trackBackToTopEvent} className={bem('scrollTop')} />}
            {paginationData && paginationData.pageNumber === paginationData.totalPages && renderCoreMediaContent(footerAd)}
        </>
    );
};

ProductListLayout.displayName = 'ProductListLayout';

ProductListLayout.propTypes = {
    productLists: PropTypes.array.isRequired,
    messages: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
    disableSPA: PropTypes.bool.isRequired,
    paginationData: PropTypes.object.isRequired,
    locale: PropTypes.string.isRequired,
    metaData: PropTypes.object.isRequired,
    baseUrl: PropTypes.string.isRequired,
    fetchMoreListing: PropTypes.func.isRequired,
    enableLoadMore: PropTypes.bool.isRequired,
    generateListingUrl: PropTypes.func.isRequired,
    ProductSummary: PropTypes.func.elementType,
    imageSrcsetSize: PropTypes.string.isRequired,
    firstFetchedPageNumber: PropTypes.number.isRequired,
    lastFetchedPageNumber: PropTypes.number.isRequired,
    footerAd: PropTypes.object,
    renderCoreMediaContent: PropTypes.func.isRequired,
    productImageAspectRatio: PropTypes.string.isRequired,
    mainContent: PropTypes.string.isRequired,
    trackLoadMoreEvent: PropTypes.func,
    setListingHeight: PropTypes.func,
    currentPage: PropTypes.number,
    trackBackToTopEvent: PropTypes.func,
    forceLogin: PropTypes.bool,
    trackProductReplacementClickEvent: PropTypes.func,
    productReplacementConfig: PropTypes.object,
    urlConfig: PropTypes.object.isRequired,
    internal_urls: PropTypes.array.isRequired,
    ssr: PropTypes.bool.isRequired,
    customPageSize: PropTypes.customPageSize,
    fullbleed: PropTypes.bool,
};

export default withOptimizely(ProductListLayout);
